/**
 * Co-editing an article: the shared Yjs document and its connection to the article's room (worker/src/article-collab.ts,
 * GET /api/dashboard/articles/collab/<id>: the editor's identity without an upgrade, the room's WebSocket with one).
 * `useCollab(articleId)` opens the room, keeps its status and who is in it, and closes it when the page goes.
 */
import { useEffect, useState } from "react";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";

export interface CollabUser { id: string; name: string; color: string }
export interface Collab {
  doc: Y.Doc; provider: WebsocketProvider; user: CollabUser;
  status: "connecting" | "live" | "offline";
  /** The document has arrived from the room: the editor can bind to it. */
  synced: boolean;
  /** The others in the room right now. */
  peers: CollabUser[];
}

const API = "/api/dashboard/articles";
/** How long to wait for the room before editing alone (the room isn't deployed, or the socket is blocked). */
export const SYNC_WAIT_MS = 8000;

/** The room for an article, or `false` when it can't be reached (edit alone), or null while it opens. */
export function useCollab(articleId: string): Collab | false | null {
  const [state, setState] = useState<Collab | false | null>(null);
  useEffect(() => {
    let doc: Y.Doc | null = null, provider: WebsocketProvider | null = null, gone = false, timer = 0;
    setState(null);
    (async () => {
      const r = await fetch(`${API}/collab/${encodeURIComponent(articleId)}`, { credentials: "include" }).catch(() => null);
      const me = r?.ok ? ((await r.json().catch(() => null)) as { user?: CollabUser } | null)?.user : null;
      if (gone || !me) { if (!gone) setState(false); return; }
      doc = new Y.Doc();
      const wss = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${API}/collab`;
      provider = new WebsocketProvider(wss, articleId, doc, { disableBc: true, maxBackoffTime: 5000 });
      const base: Collab = { doc, provider, user: me, status: "connecting", synced: false, peers: [] };
      const peersNow = () => [...provider!.awareness.getStates().entries()]
        .filter(([id]) => id !== doc!.clientID).map(([, s]) => (s as { user?: CollabUser }).user).filter((u): u is CollabUser => !!u?.name);
      const update = (patch: Partial<Collab>) => setState((cur) => (cur ? { ...cur, ...patch } : { ...base, ...patch }));
      setState(base);
      provider.on("status", ({ status }: { status: string }) => update({ status: status === "connected" ? "live" : status === "connecting" ? "connecting" : "offline" }));
      provider.on("sync", (synced: boolean) => { if (synced) { window.clearTimeout(timer); update({ synced: true }); } });
      provider.awareness.on("change", () => update({ peers: peersNow() }));
      // The room never answered: edit alone rather than stare at an empty page; the next load tries again.
      timer = window.setTimeout(() => { if (!provider?.synced) { provider?.destroy(); doc?.destroy(); provider = null; doc = null; setState(false); } }, SYNC_WAIT_MS);
    })();
    return () => { gone = true; window.clearTimeout(timer); provider?.destroy(); doc?.destroy(); };
  }, [articleId]);
  return state;
}
